import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  type CSSProperties,
  type MouseEventHandler,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import styled from "styled-components";

import { VideoCaption } from "./VideoCaption";
import { deriveVideoStatus, seekVideo, subscribeVideoEvents } from "./videoEvents";
import { VideoFrame } from "./VideoFrame";
import type {
  VideoCrossOrigin,
  VideoFrameAlign,
  VideoHandle,
  VideoObjectFit,
  VideoStatus,
} from "./types";

const DEFAULT_ASPECT_RATIO = 16 / 9;
const SEEK_TOLERANCE_SECONDS = 0.025;

const Root = styled.figure`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  width: 100%;
  min-width: 0;
  margin: 0;
`;

export interface VideoSurfaceProps {
  src: string;
  poster?: string | null;
  /** 비디오 위 라벨 (예: 카메라 채널 이름). */
  label?: ReactNode;
  /** 제목 아래 보조 캡션 줄(예: 짧은 카메라 설명). */
  subLabel?: ReactNode;
  showLabel?: boolean;
  controls?: boolean;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  playsInline?: boolean;
  preload?: "none" | "metadata" | "auto";
  crossOrigin?: VideoCrossOrigin;
  objectFit?: VideoObjectFit;
  aspectRatio?: number;
  /**
   * 폭 100% figure/caption은 유지하고 내부 media frame만 정렬한다. 기본 start.
   * viewport-fit frame을 가운데 둘 때 `frameAlign="center"`를 사용한다.
   */
  frameAlign?: VideoFrameAlign;
  /**
   * `MediaFrame.maxHeight` passthrough — aspectRatio와 함께 주면 양축 viewport-fit.
   */
  maxHeight?: string;
  timeUpdateIntervalMs?: number;
  playing?: boolean;
  currentTimeSeconds?: number;
  playbackRate?: number;
  onDurationChange?: (durationSeconds: number) => void;
  onCurrentTimeChange?: (currentTimeSeconds: number) => void;
  /** 재생 상태 변화 콜백. native `<video>` 이벤트에서 idle/buffering/playing 등을 파생한다. */
  onStatusChange?: (status: VideoStatus) => void;
  onReady?: (event: SyntheticEvent<HTMLVideoElement, Event>) => void;
  onEnded?: () => void;
  onError?: (event: SyntheticEvent<HTMLVideoElement, Event>) => void;
  onClick?: MouseEventHandler<HTMLVideoElement>;
  className?: string;
  style?: CSSProperties;
  overlay?: ReactNode;
  /**
   * 프레임 우상단 인터랙티브 액션 — 전체화면은 `MediaFullscreenButton` 프리셋 조립이
   * 표준(열림 상태·`MediaOverlay`는 consumer 소유). zoom composition에서는 이 prop 대신
   * `ZoomPanWrapper.actions`로 줄 것 — 상세는 `VideoFrame.actions` 참조.
   */
  actions?: ReactNode;
  "aria-label"?: string;
}

/**
 * 단일 native video surface. 캡션(VideoCaption) + 프레임(VideoFrame) 위에 controlled 엔진을
 * 얹는다 — controlled `playing`/`currentTimeSeconds`/`playbackRate` 반영, 자기 echo 억제,
 * `timeUpdateIntervalMs` 리포터 루프. ref는 native `<video>` 엘리먼트(`VideoHandle`)를 그대로
 * 노출하며, 내부 effect용 `localRef`와 외부 ref를 setRef로 머지한다(레포 Checkbox 패턴).
 */
export const VideoSurface = forwardRef<VideoHandle, VideoSurfaceProps>(
  (
    {
      src,
      poster,
      label,
      subLabel,
      showLabel = true,
      controls = false,
      autoPlay = false,
      muted = true,
      loop = false,
      playsInline = true,
      preload = "metadata",
      crossOrigin,
      objectFit = "contain",
      aspectRatio = DEFAULT_ASPECT_RATIO,
      frameAlign = "start",
      maxHeight,
      timeUpdateIntervalMs,
      playing,
      currentTimeSeconds,
      playbackRate,
      onDurationChange,
      onCurrentTimeChange,
      onStatusChange,
      onReady,
      onEnded,
      onError,
      onClick,
      className,
      style,
      overlay,
      actions,
      "aria-label": ariaLabel,
    },
    forwardedRef,
  ) => {
    const localRef = useRef<HTMLVideoElement | null>(null);
    const setRef = useCallback(
      (node: HTMLVideoElement | null) => {
        localRef.current = node;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef],
    );

    const onCurrentTimeChangeRef = useRef(onCurrentTimeChange);
    onCurrentTimeChangeRef.current = onCurrentTimeChange;
    const hasCurrentTimeReporter = onCurrentTimeChange !== undefined;
    const onStatusChangeRef = useRef(onStatusChange);
    onStatusChangeRef.current = onStatusChange;
    const hasStatusReporter = onStatusChange !== undefined;
    const lastStatusRef = useRef<VideoStatus | null>(null);
    // 마지막 currentTime. controlled `currentTimeSeconds`가 이 값과 같게 되돌아오면, 외부
    // 탐색(seek)이 아니라 내 에코(echo)다. 에코된 값으로 엘리먼트를 되돌려 탐색하면 timeupdate가
    // 실제 시각보다 늦는 구간(예: 클립이 아직 버퍼링 중)마다 재생과 싸우게 되므로, 에코는 무시한다.
    const lastReportedTimeRef = useRef<number | null>(null);

    useEffect(() => {
      if (playing === undefined) return;
      const video = localRef.current;
      if (!video) return;

      if (playing) {
        void video.play().catch(() => undefined);
      } else {
        video.pause();
      }
    }, [playing, src]);

    // 새 소스는 이전 보고를 무효화한다 — 다음 controlled 값은 옛 클립의 에코(echo)가 아니라
    // 외부 탐색(seek)으로 다뤄야 한다.
    useEffect(() => {
      lastReportedTimeRef.current = null;
    }, [src]);

    useEffect(() => {
      const video = localRef.current;
      if (!video || currentTimeSeconds === undefined || !Number.isFinite(currentTimeSeconds)) {
        return;
      }

      const reported = lastReportedTimeRef.current;
      const isEcho =
        reported !== null && Math.abs(currentTimeSeconds - reported) <= SEEK_TOLERANCE_SECONDS;
      if (isEcho) return;

      if (Math.abs(video.currentTime - currentTimeSeconds) > SEEK_TOLERANCE_SECONDS) {
        seekVideo(video, currentTimeSeconds);
      }
    }, [currentTimeSeconds, src]);

    useEffect(() => {
      const video = localRef.current;
      if (!video || playbackRate === undefined || !Number.isFinite(playbackRate)) return;
      video.playbackRate = playbackRate;
    }, [playbackRate, src]);

    useEffect(() => {
      // controlled 일시정지: 정지된 surface가 매 프레임 no-op rAF를 돌리지 않도록 리포터 루프를
      // 통째로 건너뛴다. (uncontrolled `playing===undefined`는 루프를 유지하되, tick 안의
      // `video.paused` 체크로 여전히 가드된다.)
      if (
        playing === false ||
        timeUpdateIntervalMs === undefined ||
        timeUpdateIntervalMs <= 0 ||
        !hasCurrentTimeReporter ||
        typeof requestAnimationFrame !== "function" ||
        typeof cancelAnimationFrame !== "function"
      ) {
        return;
      }

      let frameId = 0;
      let lastReportedAt = 0;

      const tick = (now: number) => {
        const video = localRef.current;
        if (
          video &&
          !video.paused &&
          !video.ended &&
          now - lastReportedAt >= timeUpdateIntervalMs
        ) {
          lastReportedAt = now;
          lastReportedTimeRef.current = video.currentTime;
          onCurrentTimeChangeRef.current?.(video.currentTime);
        }
        frameId = requestAnimationFrame(tick);
      };

      frameId = requestAnimationFrame(tick);
      return () => cancelAnimationFrame(frameId);
    }, [hasCurrentTimeReporter, timeUpdateIntervalMs, playing]);

    // 단일 controlled status: native `<video>` 이벤트에서 status를 파생해 변할 때만 보고한다.
    // 멀티채널 훅과 달리 telemetry store가 없어 controlled 콜백으로 흐른다(onCurrentTimeChange 패턴).
    useEffect(() => {
      if (!hasStatusReporter) return;
      const video = localRef.current;
      if (!video) return;

      lastStatusRef.current = null;
      let buffering = false;
      const report = () => {
        const status = deriveVideoStatus(video, { buffering });
        if (status === lastStatusRef.current) return;
        lastStatusRef.current = status;
        onStatusChangeRef.current?.(status);
      };
      const reportBuffering = () => {
        buffering = !video.paused && !video.ended;
        report();
      };
      const reportReady = () => {
        buffering = false;
        report();
      };
      const unsubscribe = subscribeVideoEvents(video, {
        loadedmetadata: reportReady,
        loadeddata: reportReady,
        canplay: reportReady,
        seeked: reportReady,
        emptied: reportReady,
        play: report,
        playing: reportReady,
        pause: reportReady,
        ended: reportReady,
        waiting: reportBuffering,
        stalled: reportBuffering,
        error: report,
      });
      report();
      return unsubscribe;
    }, [src, hasStatusReporter]);

    const handleLoadedMetadata = (event: SyntheticEvent<HTMLVideoElement, Event>) => {
      const video = event.currentTarget;
      const duration = event.currentTarget.duration;
      if (Number.isFinite(duration) && duration >= 0) {
        onDurationChange?.(duration);
      }
      if (currentTimeSeconds !== undefined && Number.isFinite(currentTimeSeconds)) {
        seekVideo(video, currentTimeSeconds);
      }
      if (playbackRate !== undefined && Number.isFinite(playbackRate) && playbackRate > 0) {
        video.playbackRate = playbackRate;
      }
      if (playing) {
        void video.play().catch(() => undefined);
      }
    };

    const handleTimeUpdate = (event: SyntheticEvent<HTMLVideoElement, Event>) => {
      if (timeUpdateIntervalMs !== undefined) return;
      lastReportedTimeRef.current = event.currentTarget.currentTime;
      onCurrentTimeChange?.(event.currentTarget.currentTime);
    };

    const handleEnded = () => {
      // 리포터(timeUpdate 루프/이벤트)는 `video.ended`에서 보고를 멈춰 마지막 위치가 끝 직전
      // (배속이면 interval×rate만큼)에서 고정된다 — playhead가 끝(100%)에 닿도록 종료 위치를
      // 한 번 더 알린다(SWD-418). currentTime은 ended에서 duration이지만, 정확성을 위해 유한한
      // duration을 우선한다.
      const video = localRef.current;
      if (video) {
        const end = Number.isFinite(video.duration) ? video.duration : video.currentTime;
        if (Number.isFinite(end)) {
          lastReportedTimeRef.current = end;
          onCurrentTimeChange?.(end);
        }
      }
      onEnded?.();
    };

    return (
      <Root className={className} style={style}>
        <VideoCaption as="figcaption" show={showLabel} label={label} subLabel={subLabel} />
        <VideoFrame
          ref={setRef}
          src={src}
          poster={poster}
          controls={controls}
          autoPlay={autoPlay}
          muted={muted}
          loop={loop}
          playsInline={playsInline}
          preload={preload}
          crossOrigin={crossOrigin}
          objectFit={objectFit}
          aspectRatio={aspectRatio}
          frameAlign={frameAlign}
          maxHeight={maxHeight}
          overlay={overlay}
          actions={actions}
          aria-label={ariaLabel}
          onLoadedMetadata={handleLoadedMetadata}
          onLoadedData={onReady}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          onError={onError}
          onClick={onClick}
        />
      </Root>
    );
  },
);

VideoSurface.displayName = "VideoSurface";
